/**
 * honda-adv150-mecanica - Ponto de Entrada Canônico
 * Padrão Fábio Akita: Módulos Profundos (Deep Modules) e Zero Mocks
 */

import {
  ADV150_CVT_DURABILITY_SPECS,
  ADV150_FLUID_SPECS,
  ADV150_OEM_PARTS,
  ADV150_SERVICE_LIMITS,
  ADV150_SPECIAL_TOOLS,
  ADV150_TORQUE_SPECS,
  ADV150_VEHICLE_IDENTITY,
  type CvtDurabilitySpec,
  type FluidCapacitySpec,
  type OEMPartNumberSpec,
  type ServiceLimitSpec,
  type SpecialToolSpec,
  type TorqueSpec,
  type VehicleIdentitySpec,
} from "./data/specs.js";

import {
  CVT_PARTS_3D_METROLOGY,
  Cvt3DExplosionEngine,
  type Part3DConfig,
} from "./components/cvt-3d-model.js";
import {
  CVT_DEFAULT_SPEED,
  CVT_MAX_SPEED,
  CVT_MIN_SPEED,
  CVT_OFFICIAL_PHASES,
  CVT_TIMELINE_TOTAL_DURATION_SEC,
  CvtGsapAnimator,
  type CvtPartTransformState,
  type CvtPhaseInfo,
  type CvtPhaseTimestamp,
  type CvtTimelineProgress,
  type CvtTimelineState,
  type GsapTimelineConfig,
  type GsapTweenDescriptor,
} from "./components/cvt-gsap-animator.js";
import {
  EmbeddedVideoPlayer,
  type PlayerOptions,
  type PlayerState,
} from "./components/embedded-video-player.js";
import {
  ADV150_EXPLODED_DIAGRAMS,
  ExplodedViewController,
  type ExplodedViewMeta,
} from "./components/exploded-view-controller.js";
import {
  CVT_VARIATOR_STEPS,
  InteractivePartViewer,
  type PartAssemblyStep,
  type SimulationState,
  type ViewerMode,
} from "./components/interactive-part-viewer.js";
import {
  MotorcycleVisualNavigator,
  type NavigatorOptions,
} from "./components/motorcycle-visual-navigator.js";
import {
  ADV150_ANATOMICAL_HOTSPOTS,
  type HotspotCoordinate,
  type MotorcycleAnatomyHotspot,
} from "./data/anatomy-hotspots.js";
import {
  ADV150_CANONICAL_PARTS,
  ADV150_SUBSYSTEMS,
  type CatalogPartItem,
  type CatalogSubsystemId,
  type CatalogSubsystemMeta,
} from "./data/catalog.js";
import {
  ADV150_MAINTENANCE_PLANS,
  type ChecklistItem,
  type MaintenancePlan,
} from "./data/checklists.js";
import {
  ADV150_PERFORMANCE_PARTS,
  ADV150_PHYSICAL_CONSTANTS,
  ADV150_UPGRADE_STAGES,
  type CvtKinematicsResult,
  type PerformancePartItem,
  type RollerSimulationResult,
  type UpgradeStageConfig,
  calculateCruisingRpmAtSpeed,
  calculateCvtSpeed,
  getPerformancePartsCatalog,
  getPerformanceStages,
  simulateRollerResponse,
} from "./data/cvt-performance.js";
import { ADV150_MANUAL_RESOURCES, type ManualResource } from "./data/manuais.js";
import {
  ADV150_TECHNICAL_WEBSITES,
  type TechnicalWebsiteCategory,
  type TechnicalWebsiteResource,
  getFeaturedWebsites,
  getWebsitesByCategory,
  searchTechnicalWebsites,
} from "./data/sites-mecanicos.js";
import {
  ADV150_TECHNICAL_VIDEOS,
  type TechnicalVideoItem,
  type VideoTimestamp,
  type VideoTorqueReference,
  buildEmbedUrl,
  getVideoById,
  getVideosBySubsystem,
  searchVideos,
} from "./data/videoteca.js";
import {
  Adv150MotionController,
  Adv150SoundEngine,
  type AudioSampleId,
  type IMotionController,
  type MechanicalActionType,
  type MechanicalStepEvent,
  type MotionControllerConfig,
  type MotionState,
  type QualityTier,
  type SoundEngineOptions,
} from "./engine/motion/index.js";

export {
  ADV150_TORQUE_SPECS,
  ADV150_SERVICE_LIMITS,
  ADV150_FLUID_SPECS,
  ADV150_OEM_PARTS,
  ADV150_SPECIAL_TOOLS,
  ADV150_VEHICLE_IDENTITY,
  ADV150_CVT_DURABILITY_SPECS,
  ADV150_MAINTENANCE_PLANS,
  ADV150_MANUAL_RESOURCES,
  ADV150_TECHNICAL_WEBSITES,
  ADV150_PERFORMANCE_PARTS,
  ADV150_PHYSICAL_CONSTANTS,
  ADV150_UPGRADE_STAGES,
  ADV150_CANONICAL_PARTS,
  ADV150_SUBSYSTEMS,
  ADV150_ANATOMICAL_HOTSPOTS,
  ADV150_EXPLODED_DIAGRAMS,
  CVT_VARIATOR_STEPS,
  CVT_PARTS_3D_METROLOGY,
  CVT_OFFICIAL_PHASES,
  CVT_TIMELINE_TOTAL_DURATION_SEC,
  CVT_MIN_SPEED,
  CVT_MAX_SPEED,
  CVT_DEFAULT_SPEED,
  InteractivePartViewer,
  Cvt3DExplosionEngine,
  CvtGsapAnimator,
  MotorcycleVisualNavigator,
  ExplodedViewController,
  EmbeddedVideoPlayer,
  searchTechnicalWebsites,
  getWebsitesByCategory,
  getFeaturedWebsites,
  calculateCvtSpeed,
  calculateCruisingRpmAtSpeed,
  simulateRollerResponse,
  getPerformancePartsCatalog,
  getPerformanceStages,
  ADV150_TECHNICAL_VIDEOS,
  buildEmbedUrl,
  getVideoById,
  getVideosBySubsystem,
  searchVideos,
  type TorqueSpec,
  type ServiceLimitSpec,
  type FluidCapacitySpec,
  type OEMPartNumberSpec,
  type SpecialToolSpec,
  type VehicleIdentitySpec,
  type CvtDurabilitySpec,
  type ChecklistItem,
  type MaintenancePlan,
  type ManualResource,
  type TechnicalWebsiteCategory,
  type TechnicalWebsiteResource,
  type CatalogPartItem,
  type CatalogSubsystemId,
  type CatalogSubsystemMeta,
  type HotspotCoordinate,
  type MotorcycleAnatomyHotspot,
  type NavigatorOptions,
  type ExplodedViewMeta,
  type PartAssemblyStep,
  type SimulationState,
  type ViewerMode,
  type Part3DConfig,
  type CvtPhaseInfo,
  type CvtPhaseTimestamp,
  type CvtTimelineProgress,
  type CvtPartTransformState,
  type CvtTimelineState,
  type GsapTimelineConfig,
  type GsapTweenDescriptor,
  type PerformancePartItem,
  type UpgradeStageConfig,
  type CvtKinematicsResult,
  type RollerSimulationResult,
  type TechnicalVideoItem,
  type VideoTimestamp,
  type VideoTorqueReference,
  type PlayerOptions,
  type PlayerState,
  Adv150MotionController,
  Adv150SoundEngine,
  type AudioSampleId,
  type IMotionController,
  type MechanicalActionType,
  type MechanicalStepEvent,
  type MotionControllerConfig,
  type MotionState,
  type QualityTier,
  type SoundEngineOptions,
};

/**
 * Retorna os dados oficiais de identificação e registro da motocicleta (CRLV-e)
 */
export function getVehicleIdentity(): VehicleIdentitySpec {
  return ADV150_VEHICLE_IDENTITY;
}

/**
 * Busca rápida de especificações de torque por termo de busca (componente ou categoria)
 */
export function searchTorqueSpec(query: string): TorqueSpec[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return ADV150_TORQUE_SPECS;

  return ADV150_TORQUE_SPECS.filter(
    (spec) =>
      spec.component.toLowerCase().includes(normalized) ||
      spec.criticalNotes.toLowerCase().includes(normalized) ||
      spec.category.toLowerCase().includes(normalized) ||
      spec.socketSize.toLowerCase().includes(normalized) ||
      spec.threadSize.toLowerCase().includes(normalized),
  );
}

/**
 * Busca rápida de peças OEM oficiais por termo (código, nome, categoria ou descrição)
 */
export function searchOEMPart(query: string): OEMPartNumberSpec[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return ADV150_OEM_PARTS;

  return ADV150_OEM_PARTS.filter(
    (part) =>
      part.partNumber.toLowerCase().includes(normalized) ||
      part.name.toLowerCase().includes(normalized) ||
      part.category.toLowerCase().includes(normalized) ||
      part.description.toLowerCase().includes(normalized),
  );
}

/**
 * Busca rápida de ferramentas especiais e instrumentos de metrologia por termo
 */
export function searchSpecialTool(query: string): SpecialToolSpec[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return ADV150_SPECIAL_TOOLS;

  return ADV150_SPECIAL_TOOLS.filter(
    (tool) =>
      tool.codeOrStandard.toLowerCase().includes(normalized) ||
      tool.name.toLowerCase().includes(normalized) ||
      tool.category.toLowerCase().includes(normalized) ||
      tool.application.toLowerCase().includes(normalized) ||
      tool.criticalUsageNote.toLowerCase().includes(normalized),
  );
}

/**
 * Retorna o plano de revisão adequado ou mais próximo com base na quilometragem atual do odômetro
 */
export function getRecommendedMaintenancePlan(odometerKm: number): MaintenancePlan {
  if (odometerKm <= 2500) {
    const plan = ADV150_MAINTENANCE_PLANS.find((p) => p.km === 1000);
    if (plan) return plan;
  }

  // Intervalos de 6.000 em 6.000 km
  const standardKmCheckpoints = [6000, 12000, 18000, 24000, 36000];
  const cycleKm = odometerKm % 36000 || 36000;

  let closestCheckpoint = 6000;
  let minDiff = Number.POSITIVE_INFINITY;

  for (const km of standardKmCheckpoints) {
    const diff = Math.abs(cycleKm - km);
    if (diff < minDiff) {
      minDiff = diff;
      closestCheckpoint = km;
    }
  }

  const foundPlan = ADV150_MAINTENANCE_PLANS.find((p) => p.km === closestCheckpoint);
  const defaultPlan = ADV150_MAINTENANCE_PLANS[0];
  if (foundPlan) return foundPlan;
  if (defaultPlan) return defaultPlan;
  throw new Error("Nenhum plano de manutenção configurado");
}

/**
 * Validação técnica de limite de desgaste para medição de campo
 */
export function evaluatePartWear(
  partId: string,
  measuredValueMm: number,
): { isWithinLimit: boolean; status: "ok" | "rejected"; message: string } {
  switch (partId) {
    case "correia": {
      // Limite oficial Honda ADV 150: 18,5 mm
      const ok = measuredValueMm >= 18.5;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Largura de ${measuredValueMm.toFixed(2)} mm está dentro da tolerância (limite mín: 18,5 mm).`
          : `REJEIÇÃO: Largura de ${measuredValueMm.toFixed(2)} mm está abaixo do limite de serviço (18,5 mm). Substituição obrigatória da correia 23100-K97-T01.`,
      };
    }
    case "roletes": {
      // Limite oficial Honda: 19,5 mm
      const ok = measuredValueMm >= 19.5;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Diâmetro de ${measuredValueMm.toFixed(2)} mm está dentro da tolerância (limite mín: 19,5 mm).`
          : `REJEIÇÃO: Diâmetro de ${measuredValueMm.toFixed(2)} mm está abaixo do limite de serviço (19,5 mm). Substituir jogo de 6 roletes 21g.`,
      };
    }
    case "campana": {
      // Limite oficial: diâmetro máx 125,5 mm
      const ok = measuredValueMm <= 125.5;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Diâmetro interno de ${measuredValueMm.toFixed(2)} mm está dentro do limite (máx: 125,5 mm).`
          : `REJEIÇÃO: Diâmetro interno de ${measuredValueMm.toFixed(2)} mm excedeu o limite de serviço (125,5 mm). Trocar campana de embreagem.`,
      };
    }
    case "sapatas": {
      // Limite mínimo: 2,0 mm
      const ok = measuredValueMm >= 2.0;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Espessura de ${measuredValueMm.toFixed(2)} mm está dentro do limite (mín: 2,0 mm).`
          : `REJEIÇÃO: Espessura de ${measuredValueMm.toFixed(2)} mm atingiu o limite de serviço (2,0 mm). Substituir sapatas de embreagem.`,
      };
    }
    case "disco_dianteiro":
    case "disco_traseiro": {
      // Limite oficial de descarte: 3,5 mm
      const ok = measuredValueMm >= 3.5;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Espessura de ${measuredValueMm.toFixed(2)} mm está dentro da tolerância (limite mín de descarte: 3,5 mm).`
          : `REJEIÇÃO: Espessura de ${measuredValueMm.toFixed(2)} mm atingiu o limite de serviço de descarte (3,5 mm - MIN. TH. 3.5 mm). Substituição obrigatória do disco de freio Wave.`,
      };
    }
    case "contra_mola": {
      // Limite oficial: 134,0 mm (comprimento livre)
      const ok = measuredValueMm >= 134.0;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Comprimento livre de ${measuredValueMm.toFixed(1)} mm está dentro do limite (limite mín: 134,0 mm).`
          : `REJEIÇÃO: Comprimento livre de ${measuredValueMm.toFixed(1)} mm está abaixo do limite de serviço (134,0 mm). Substituir mola de contraste 23233-K97-T00.`,
      };
    }
    case "bucha_primaria": {
      // Limite oficial: 23,960 mm (diâmetro externo)
      const ok = measuredValueMm >= 23.96;
      return {
        isWithinLimit: ok,
        status: ok ? "ok" : "rejected",
        message: ok
          ? `Diâmetro externo de ${measuredValueMm.toFixed(3)} mm está dentro da tolerância (limite mín: 23,960 mm).`
          : `REJEIÇÃO: Diâmetro externo de ${measuredValueMm.toFixed(3)} mm está abaixo do limite de serviço (23,960 mm). Substituir bucha central do variador 22105-K97-T00.`,
      };
    }
    default:
      return {
        isWithinLimit: false,
        status: "rejected",
        message: `Componente desconhecido para validação: ${partId}`,
      };
  }
}

/**
 * Busca rápida de especificações de durabilidade de componentes do CVT por termo ou ID
 */
export function searchCvtDurability(query: string): CvtDurabilitySpec[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return ADV150_CVT_DURABILITY_SPECS;

  return ADV150_CVT_DURABILITY_SPECS.filter(
    (item) =>
      item.id.toLowerCase().includes(normalized) ||
      item.component.toLowerCase().includes(normalized) ||
      item.partNumber.toLowerCase().includes(normalized) ||
      item.symptoms.toLowerCase().includes(normalized),
  );
}
